/**
 * Default widget registrations — executed once on app startup.
 *
 * Cross-References:
 *   - FR-DASH-002: Default widgets
 *   - FR-DASH-003: Widget registration system
 */
import { registerWidget } from '@/services/widgetRegistry';
import { WelcomeWidget } from './WelcomeWidget';
import { QuickActionsWidget } from './QuickActionsWidget';
import { DirectorySearchWidget } from './DirectorySearchWidget';
import { AccessReviewsWidget } from './AccessReviewsWidget';
import RecentPagesWidget from '@/modules/cm/components/RecentPagesWidget';
// CW-SF-M5 — Salesforce dashboard widgets (Personal zone).
import { SfMyOpenOpportunitiesWidget } from '@/modules/cw/components/widgets/SfMyOpenOpportunitiesWidget';
import { SfPipelineByStageWidget } from '@/modules/cw/components/widgets/SfPipelineByStageWidget';
import { SfClosingThisQuarterWidget } from '@/modules/cw/components/widgets/SfClosingThisQuarterWidget';
import { SfMyRecentActivitiesWidget } from '@/modules/cw/components/widgets/SfMyRecentActivitiesWidget';
// CW-SF-D1 — Salesforce dashboard widgets (Department zone).
import { SfTeamPipelineByStageWidget } from '@/modules/cw/components/widgets/SfTeamPipelineByStageWidget';
import { SfTeamClosingThisQuarterWidget } from '@/modules/cw/components/widgets/SfTeamClosingThisQuarterWidget';
import { SfRecentWinsWidget } from '@/modules/cw/components/widgets/SfRecentWinsWidget';
import { SfPipelineByOwnerWidget } from '@/modules/cw/components/widgets/SfPipelineByOwnerWidget';
import { SfTopAccountsByPipelineWidget } from '@/modules/cw/components/widgets/SfTopAccountsByPipelineWidget';
import { SfStalledDealsWidget } from '@/modules/cw/components/widgets/SfStalledDealsWidget';
import { SfTeamRecentActivityWidget } from '@/modules/cw/components/widgets/SfTeamRecentActivityWidget';
// CW-SF-R2 — Salesforce dashboard widgets (CEO role zone).
import { SfForecastByCategoryWidget } from '@/modules/cw/components/widgets/SfForecastByCategoryWidget';
import { SfPipelineByRegionWidget } from '@/modules/cw/components/widgets/SfPipelineByRegionWidget';
import { SfBookingsQoQWidget } from '@/modules/cw/components/widgets/SfBookingsQoQWidget';
import { SfWinRateWidget } from '@/modules/cw/components/widgets/SfWinRateWidget';
import { SfTopAccountsAllTimeWidget } from '@/modules/cw/components/widgets/SfTopAccountsAllTimeWidget';
import { SfLargeDealsAttentionWidget } from '@/modules/cw/components/widgets/SfLargeDealsAttentionWidget';
import { SfSalesCycleTrendWidget } from '@/modules/cw/components/widgets/SfSalesCycleTrendWidget';
import { SfAttainmentWidget } from '@/modules/cw/components/widgets/SfAttainmentWidget';
import { SfPipelineCoverageWidget } from '@/modules/cw/components/widgets/SfPipelineCoverageWidget';
// VM-M6-6 — Vendor Management widgets (Personal zone).
import { MyRecentReceiptsWidget } from '@/modules/vm/components/MyRecentReceiptsWidget';

export function registerDefaultWidgets(): void {
  registerWidget({
    type: 'welcome',
    title: 'Welcome',
    component: WelcomeWidget,
    defaultSize: { w: 2, h: 1 },
    description: 'Personalised greeting and getting-started tips.',
    icon: 'hand-wave',
  });

  registerWidget({
    type: 'quick-actions',
    title: 'Quick Actions',
    component: QuickActionsWidget,
    defaultSize: { w: 1, h: 1 },
    description: 'Shortcuts to common tasks (permission-filtered).',
    icon: 'zap',
  });

  registerWidget({
    type: 'directory-search',
    title: 'People Search',
    component: DirectorySearchWidget,
    defaultSize: { w: 1, h: 1 },
    description: 'Quick inline search for colleagues.',
    icon: 'search',
  });

  registerWidget({
    type: 'recent-pages',
    title: 'What\'s New',
    component: RecentPagesWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Recently updated content pages across the org.',
    icon: 'file-text',
  });

  // SEC-3 TKT-7 — access-review counters for admins/reviewers.
  registerWidget({
    type: 'access-reviews',
    title: 'Access Reviews',
    component: AccessReviewsWidget,
    defaultSize: { w: 1, h: 1 },
    description: 'Reviews due in the next 7 days + overdue counts.',
    icon: 'shield-check',
  });

  // ── CW-SF-M5 — Salesforce widgets ────────────────────────────
  registerWidget({
    type: 'sf-my-open-opportunities',
    title: 'My Open Opportunities',
    component: SfMyOpenOpportunitiesWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Your top 10 open Salesforce opportunities by close date.',
    icon: 'briefcase',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-pipeline-by-stage',
    title: 'Pipeline by Stage',
    component: SfPipelineByStageWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Your open Salesforce pipeline grouped by stage.',
    icon: 'bar-chart-2',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-closing-this-quarter',
    title: 'Closing This Quarter',
    component: SfClosingThisQuarterWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Open Salesforce opportunities closing this calendar quarter.',
    icon: 'calendar',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-my-recent-activities',
    title: 'My Recent SF Activities',
    component: SfMyRecentActivitiesWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Your Salesforce tasks and events from the past 7 days.',
    icon: 'check-square',
    refreshInterval: 5 * 60,
  });

  // ── CW-SF-D1 — Salesforce team widgets (Department zone) ─────
  registerWidget({
    type: 'sf-team-pipeline-by-stage',
    title: 'Team Pipeline by Stage',
    component: SfTeamPipelineByStageWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Open team pipeline grouped by stage.',
    icon: 'bar-chart-2',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-closing-this-quarter',
    title: 'Closing This Quarter — Team',
    component: SfTeamClosingThisQuarterWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Open team opportunities closing in the current fiscal quarter.',
    icon: 'calendar',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-recent-wins',
    title: 'Recent Wins',
    component: SfRecentWinsWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Team closed-won opportunities in the last 30 days.',
    icon: 'trophy',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-pipeline-by-owner',
    title: 'Pipeline by Owner',
    component: SfPipelineByOwnerWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Open pipeline distribution across reps.',
    icon: 'users',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-top-accounts',
    title: 'Top Accounts by Open Pipeline',
    component: SfTopAccountsByPipelineWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Accounts with the most open pipeline across the team.',
    icon: 'building-2',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-stalled-deals',
    title: 'Stalled Deals',
    component: SfStalledDealsWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Open opportunities with no activity in the last 14 days.',
    icon: 'alert-triangle',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-team-recent-activity',
    title: 'Recent Team Activity',
    component: SfTeamRecentActivityWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Latest Salesforce activity across the team.',
    icon: 'activity',
    refreshInterval: 5 * 60,
  });

  // ── CW-SF-R2 — Salesforce CEO-tier widgets (Role zone) ────────
  registerWidget({
    type: 'sf-forecast-by-category',
    title: 'Forecast by Category',
    component: SfForecastByCategoryWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Opps pivoted by Salesforce ForecastCategory for the fiscal quarter.',
    icon: 'pie-chart',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-pipeline-by-region',
    title: 'Pipeline by Region',
    component: SfPipelineByRegionWidget,
    defaultSize: { w: 4, h: 1 },
    description: 'Open pipeline scalar per region via SF ListView membership.',
    icon: 'globe-2',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-bookings-q-over-q',
    title: 'Bookings — This Q vs Last Q',
    component: SfBookingsQoQWidget,
    defaultSize: { w: 2, h: 1 },
    description: 'Closed-won totals comparing current and prior fiscal quarter.',
    icon: 'trending-up',
    refreshInterval: 5 * 60,
    expandable: false,
  });
  registerWidget({
    type: 'sf-win-rate',
    title: 'Win Rate (90d)',
    component: SfWinRateWidget,
    defaultSize: { w: 2, h: 1 },
    description: 'Won / (Won + Lost) across closed opps in the trailing 90 days.',
    icon: 'percent',
    refreshInterval: 5 * 60,
    expandable: false,
  });
  registerWidget({
    type: 'sf-top-accounts-all-time',
    title: 'Top 10 Accounts (All-Time)',
    component: SfTopAccountsAllTimeWidget,
    defaultSize: { w: 2, h: 3 },
    description: 'Accounts ranked by all-time closed-won amount.',
    icon: 'crown',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-large-deals-attention',
    title: 'Large Deals Needing Attention',
    component: SfLargeDealsAttentionWidget,
    defaultSize: { w: 2, h: 3 },
    description: 'Open opps over $500K in late stages — sorted by close date.',
    icon: 'flag',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-sales-cycle-trend',
    title: 'Sales Cycle Trend',
    component: SfSalesCycleTrendWidget,
    defaultSize: { w: 2, h: 2 },
    description: 'Avg days from creation to close for won opps, last 4 quarters.',
    icon: 'clock',
    refreshInterval: 5 * 60,
  });
  registerWidget({
    type: 'sf-attainment',
    title: 'Attainment This Quarter',
    component: SfAttainmentWidget,
    defaultSize: { w: 2, h: 1 },
    description: 'Closed-won this fiscal Q against the quarterly quota.',
    icon: 'target',
    refreshInterval: 5 * 60,
    expandable: false,
  });
  registerWidget({
    type: 'sf-pipeline-coverage',
    title: 'Pipeline Coverage Ratio',
    component: SfPipelineCoverageWidget,
    defaultSize: { w: 2, h: 1 },
    description: 'Open pipeline / quarterly quota — color-coded.',
    icon: 'gauge',
    refreshInterval: 5 * 60,
    expandable: false,
  });

  // ── VM-M6-6 — Vendor Management widgets ──────────────────────
  registerWidget({
    type: 'vm-my-recent-receipts',
    title: 'My Recent Receipts',
    component: MyRecentReceiptsWidget,
    defaultSize: { w: 1, h: 2 },
    description: 'Your last 5 submitted receipts and any pending info requests.',
    icon: 'receipt',
  });
}
